// ═══════════════════════════════════════════════════════════
// Invoices API
// Payment processor is STUBBED — no real Stripe integration.
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord } from './client';
type AnonOpts = { anonymous?: boolean };
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { createAuditEntry } from './auditLog';

const FR = FIELDS.requests;

export type InvoiceStatus = 'draft' | 'estimateOnly' | 'sent' | 'paid' | 'void';
export type FeeWaiverStatus = 'pending' | 'approved' | 'denied' | null;
export type InvoiceItemType = 'search' | 'copy' | 'certification' | 'redaction' | 'delivery' | 'other';

export interface InvoiceItem {
  recordId:    number;
  invoiceId:   number;
  type:        InvoiceItemType;
  description: string;
  quantity:    number;
  unitPrice:   number;
  total:       number;
  staffUserId?:string;
  hours?:      number;
}

export interface Invoice {
  recordId:       number;
  requestId:      number;
  requesterId?:   number;
  status:         InvoiceStatus;
  subtotal:       number;
  total:          number;
  estimateSentAt?: string;
  invoiceSentAt?:  string;
  paidAt?:         string;
  paymentMethod?:  string;
  stripePaymentId?:string;
  pdfUrl?:         string;
  notes?:          string;
  feeWaiverStatus: FeeWaiverStatus;
  feeWaiverReason?: string;
  items:           InvoiceItem[];
}

const FI  = FIELDS.invoices;
const FII = FIELDS.invoiceItems;

function rowToItem(row: Record<string, unknown>): InvoiceItem {
  return {
    recordId:    row['record_id'] as number,
    invoiceId:   Number(row[FII.invoiceId] ?? 0),
    type:        (row[FII.type] as InvoiceItemType) ?? 'other',
    description: String(row[FII.description] ?? ''),
    quantity:    Number(row[FII.quantity] ?? 1),
    unitPrice:   Number(row[FII.unitPrice] ?? 0),
    total:       Number(row[FII.total] ?? 0),
    staffUserId: row[FII.staffUserId] as string | undefined,
    hours:       row[FII.hours] as number | undefined,
  };
}

const WAIVER_RE = /^\[WAIVER:(pending|approved|denied)(?::([^\]]*))?\]/;

function parseWaiver(notes: string | undefined): { status: FeeWaiverStatus; reason?: string; cleanNotes: string } {
  const raw = notes ?? '';
  const m = WAIVER_RE.exec(raw);
  if (!m) return { status: null, cleanNotes: raw };
  return {
    status: m[1] as FeeWaiverStatus,
    reason: m[2] || undefined,
    cleanNotes: raw.slice(m[0].length).trimStart(),
  };
}

function encodeWaiver(status: FeeWaiverStatus, reason?: string, existingNotes?: string): string {
  const clean = (existingNotes ?? '').replace(WAIVER_RE, '').trimStart();
  if (!status) return clean;
  const tag = reason ? `[WAIVER:${status}:${reason}]` : `[WAIVER:${status}]`;
  return clean ? `${tag} ${clean}` : tag;
}

function rowToInvoice(row: Record<string, unknown>, items: InvoiceItem[] = []): Invoice {
  const rawNotes = row[FI.notes] as string | undefined;
  const { status: feeWaiverStatus, reason: feeWaiverReason, cleanNotes } = parseWaiver(rawNotes);
  return {
    recordId:        row['record_id'] as number,
    requestId:       Number(row[FI.requestId] ?? 0),
    requesterId:     row[FI.requesterId] as number | undefined,
    status:          (row[FI.status] as InvoiceStatus) ?? 'draft',
    subtotal:        Number(row[FI.subtotal] ?? 0),
    total:           Number(row[FI.total] ?? 0),
    estimateSentAt:  row[FI.estimateSentAt] as string | undefined,
    invoiceSentAt:   row[FI.invoiceSentAt]  as string | undefined,
    paidAt:          row[FI.paidAt]         as string | undefined,
    paymentMethod:   row[FI.paymentMethod]  as string | undefined,
    stripePaymentId: row[FI.stripePaymentId]as string | undefined,
    pdfUrl:          row[FI.pdfUrl]         as string | undefined,
    notes:           cleanNotes || undefined,
    feeWaiverStatus,
    feeWaiverReason,
    items,
  };
}

export async function getInvoiceForRequest(requestId: number, opts?: AnonOpts): Promise<Invoice | null> {
  const all = await listInvoicesForRequest(requestId, opts);
  // Return the most recently created (highest recordId) active invoice
  const active = all.filter(i => i.status !== 'void').sort((a, b) => b.recordId - a.recordId);
  return active[0] ?? all[0] ?? null;
}

/** Returns all invoices for a request, each with their line items. */
export async function listInvoicesForRequest(requestId: number, opts?: AnonOpts): Promise<Invoice[]> {
  const { records } = await queryRecords(TABLES.invoices, {
    filters: [{ field: FI.requestId, operator: 'eq', value: requestId }],
    limit: 50,
  }, opts);

  const rows = records as Record<string, unknown>[];
  // Sort newest first
  rows.sort((a, b) => (b['record_id'] as number) - (a['record_id'] as number));

  const invoices: Invoice[] = [];
  for (const row of rows) {
    const invoiceId = row['record_id'] as number;
    const { records: itemRows } = await queryRecords(TABLES.invoiceItems, {
      filters: [{ field: FII.invoiceId, operator: 'eq', value: invoiceId }],
      limit: 100,
    }, opts);
    invoices.push(rowToInvoice(row, (itemRows as Record<string, unknown>[]).map(rowToItem)));
  }
  return invoices;
}

export async function requestFeeWaiver(
  invoiceId:  number,
  reason:     string,
  existingNotes: string | undefined,
  opts?: AnonOpts,
): Promise<void> {
  const newNotes = encodeWaiver('pending', reason.trim() || undefined, existingNotes);
  await updateRecord(TABLES.invoices, invoiceId, { [FI.notes]: newNotes }, opts);
}

/**
 * Process a (stubbed) payment — marks invoice paid, updates request to pendingReview.
 * Called from the public portal PaymentPage with { anonymous: true }.
 */
export async function processPayment(
  invoiceId:     number,
  requestId:     number,
  cardBrand:     string,
  last4:         string,
  opts?: AnonOpts,
): Promise<void> {
  const now = new Date().toISOString();
  // Generate a fake transaction reference
  const txRef = 'TXN-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();

  await updateRecord(TABLES.invoices, invoiceId, {
    [FI.status]:          'paid',
    [FI.paidAt]:          now,
    [FI.paymentMethod]:   `${cardBrand} ending ${last4}`,
    [FI.stripePaymentId]: txRef,
  }, opts);

  // Move request forward
  await updateRecord(TABLES.requests, requestId, {
    [FR.status]: 'pendingReview',
  }, opts);
}

export async function resolveFeeWaiver(
  invoiceId:     number,
  decision:      'approved' | 'denied',
  existingNotes: string | undefined,
  actorEmail?:   string,
  requestId?:    number,
): Promise<Invoice> {
  // On approval, zero out the total
  const patch: Record<string, unknown> = {
    [FI.notes]: encodeWaiver(decision, undefined, existingNotes),
  };
  if (decision === 'approved') {
    patch[FI.subtotal] = 0;
    patch[FI.total]    = 0;
    patch[FI.status]   = 'void';
  }
  const row = await updateRecord(TABLES.invoices, invoiceId, patch);

  if (requestId) {
    await createAuditEntry({
      requestId,
      userEmail: actorEmail,
      action:    `invoice.feeWaiver.${decision}`,
      details:   JSON.stringify({ invoiceId }),
    });
  }

  const { records: itemRows } = await queryRecords(TABLES.invoiceItems, {
    filters: [{ field: FII.invoiceId, operator: 'eq', value: invoiceId }],
    limit: 100,
  });
  const items = (itemRows as Record<string, unknown>[]).map(rowToItem);
  return rowToInvoice(row as Record<string, unknown>, items);
}

export interface CreateInvoiceInput {
  requestId:  number;
  requesterId?:number;
  notes?:     string;
  items: {
    type:        InvoiceItemType;
    description: string;
    quantity:    number;
    unitPrice:   number;
    hours?:      number;
    staffUserId?:string;
  }[];
}

export async function createInvoice(
  input: CreateInvoiceInput,
  actorEmail?: string,
): Promise<Invoice> {
  // Calculate totals
  const itemTotals = input.items.map(it => ({
    ...it,
    total: parseFloat((it.quantity * it.unitPrice).toFixed(2)),
  }));
  const subtotal = parseFloat(itemTotals.reduce((s, it) => s + it.total, 0).toFixed(2));
  const total    = subtotal; // no tax for now

  const row = await createRecord(TABLES.invoices, {
    [FI.requestId]:  input.requestId,
    [FI.requesterId]:input.requesterId ?? null,
    [FI.status]:     'draft',
    [FI.subtotal]:   subtotal,
    [FI.total]:      total,
    [FI.notes]:      input.notes ?? '',
  });

  const invoiceId = (row as Record<string, unknown>)['record_id'] as number;

  // Create line items
  const createdItems: InvoiceItem[] = [];
  for (const it of itemTotals) {
    const itemRow = await createRecord(TABLES.invoiceItems, {
      [FII.invoiceId]:   invoiceId,
      [FII.type]:        it.type,
      [FII.description]: it.description,
      [FII.quantity]:    it.quantity,
      [FII.unitPrice]:   it.unitPrice,
      [FII.total]:       it.total,
      [FII.staffUserId]: it.staffUserId ?? '',
      [FII.hours]:       it.hours ?? 0,
    });
    createdItems.push(rowToItem(itemRow as Record<string, unknown>));
  }

  await createAuditEntry({
    requestId:  input.requestId,
    userEmail:  actorEmail,
    action:     'invoice.created',
    details:    JSON.stringify({ invoiceId, total }),
  });

  return rowToInvoice(row as Record<string, unknown>, createdItems);
}

export async function updateInvoiceStatus(
  invoiceId: number,
  status: InvoiceStatus,
  actorEmail?: string,
  requestId?: number,
): Promise<Invoice> {
  const patch: Record<string, unknown> = { [FI.status]: status };
  const now = new Date().toISOString();

  if (status === 'sent')         patch[FI.invoiceSentAt]  = now;
  if (status === 'estimateOnly') patch[FI.estimateSentAt] = now;
  if (status === 'paid')         patch[FI.paidAt]         = now;

  const row = await updateRecord(TABLES.invoices, invoiceId, patch);

  // When an invoice is sent, move the request into awaitingPayment so the
  // customer portal surfaces the payment notice immediately.
  if (status === 'sent' && requestId) {
    await updateRecord(TABLES.requests, requestId, { [FR.status]: 'awaitingPayment' });
  }

  if (requestId) {
    await createAuditEntry({
      requestId,
      userEmail: actorEmail,
      action:    status === 'paid' ? 'invoice.paid' : 'invoice.sent',
      details:   JSON.stringify({ invoiceId, status }),
    });
  }

  // Reload with items
  const { records: itemRows } = await queryRecords(TABLES.invoiceItems, {
    filters: [{ field: FII.invoiceId, operator: 'eq', value: invoiceId }],
    limit: 100,
  });
  const items = (itemRows as Record<string, unknown>[]).map(rowToItem);
  return rowToInvoice(row as Record<string, unknown>, items);
}

export async function addInvoiceItem(
  invoiceId: number,
  item: Omit<InvoiceItem, 'recordId' | 'invoiceId' | 'total'>,
): Promise<InvoiceItem> {
  const total = parseFloat((item.quantity * item.unitPrice).toFixed(2));
  const row = await createRecord(TABLES.invoiceItems, {
    [FII.invoiceId]:   invoiceId,
    [FII.type]:        item.type,
    [FII.description]: item.description,
    [FII.quantity]:    item.quantity,
    [FII.unitPrice]:   item.unitPrice,
    [FII.total]:       total,
    [FII.staffUserId]: item.staffUserId ?? '',
    [FII.hours]:       item.hours ?? 0,
  });
  return rowToItem(row as Record<string, unknown>);
}

// Recalculate and update invoice totals
export async function recalcInvoice(invoiceId: number, items: InvoiceItem[]): Promise<void> {
  const subtotal = parseFloat(items.reduce((s, it) => s + it.total, 0).toFixed(2));
  await updateRecord(TABLES.invoices, invoiceId, {
    [FI.subtotal]: subtotal,
    [FI.total]:    subtotal,
  });
}

// ── Action-item queries ────────────────────────────────────

export interface PendingFeeWaiver {
  invoiceId: number;
  requestId: number;
  reason?:   string;
  total:     number;
}

/** Returns all invoices currently waiting for a fee-waiver decision. */
export async function listPendingFeeWaivers(): Promise<PendingFeeWaiver[]> {
  const { records } = await queryRecords(TABLES.invoices, {
    filters: [{ field: FI.notes, operator: 'contains', value: '[WAIVER:pending' }],
    limit: 200,
  });
  return (records as Record<string, unknown>[])
    .map(row => {
      const { reason } = parseWaiver(String(row[FI.notes] ?? ''));
      return {
        invoiceId: row['record_id'] as number,
        requestId: Number(row[FI.requestId] ?? 0),
        reason,
        total:     Number(row[FI.total] ?? 0),
      };
    })
    .filter(w => w.requestId > 0);
}
